import { ChangeEvent, useEffect, useState } from 'react';
import './styles.css';
import LoadingApp from '../../../components/LoadingApp';
import { getAnexoProntuario, getListaReceitaDigital } from '../../../services/ProntuarioService';
import { ReceitaDigitalData } from '../../../interfaces/props/SaudeOnline/ReceitaDigital/ReceitaDigitalData';
import TabelaReceitas from './TabelaReceitas';
import { base64toBlob } from '../../../utils/Base64ToBlob';
import { Button, TextField, Typography } from '@mui/material';
import ModalFeedBack from '../../../components/ModalFeedBack';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import { sxButton, sxCalendar, sxCalendarContrast, sxTextField } from './sxProps';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import { getDayName } from '../../../utils/DateFormat';
import { ptBR } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PageTitle from '../../../components/Accessibility/PageTitle';
import DateInput from '../../../components/DateInput';

const stringToDate = (date: string): Date => {
  const partes = date.split("/");
  const dia = parseInt(partes[0], 10);
  const mes = parseInt(partes[1], 10) - 1;
  const ano = parseInt(partes[2], 10);
  return new Date(ano, mes, dia);
};

function ReceitaDigital() {
  const [isLoadingData, setLoadingData] = useState<boolean>(false);
  const [isLoadingReceita, setLoadingReceita] = useState<boolean>(false);
  const [receitas, setReceitas] = useState<ReceitaDigitalData[]>([]);
  const [pesquisar, setPesquisar] = useState<string>("");
  const [dataInicio, setDataInicio] = useState<Dayjs | null>(null);
  const [dataFim, setDataFim] = useState<Dayjs | null>(null);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorModalMessage, setErrorModalMessage] = useState<string>("");
  const [filteredReceitas, setFilteredReceitas] = useState<ReceitaDigitalData[]>([]);

  const accessibility = useAccessibility();

  useEffect(() => {
    setLoadingData(true);
    getListaReceitaDigital()
      .then(response => {
        console.log(response);
        setReceitas(response || []);
        handleFilter(response || [], pesquisar, dataInicio, dataFim);
      })
      .catch(err => {
        console.log(err);
        setErrorModalMessage('Erro ao buscar dados. Tente novamente mais tarde.')
        setShowErrorModal(true);
      })
      .finally(() => {
        setLoadingData(false);
      })
    }, [])

  const handleShowPdf = (idAnexo: string) => {
    setLoadingReceita(true);
    getAnexoProntuario(idAnexo)
    .then(pdf => {
        if (pdf) {
          const blob = base64toBlob(pdf.split(',')[1]);
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl);
        }
      })
      .catch(err => {
        console.log(err);
        setErrorModalMessage('Erro ao buscar receita digital. Tente novamente mais tarde.')
        setShowErrorModal(true);
      })
      .finally(() => {
        setLoadingReceita(false);
      })
    }

  const handlePesquisarChange = (event: ChangeEvent<HTMLInputElement | HTMLInputElement>) => {
    setPesquisar(event.target.value);
  };

  const handleFilter = (receitas: ReceitaDigitalData[], pesquisar: string, dataInicio: Dayjs | null, dataFim: Dayjs | null) => {
    const filteredReceitas = receitas.filter((receita) => {
      let result = true;
      if (pesquisar) result &&= receita.nomeSolicitante.toLowerCase().includes(pesquisar.toLowerCase()) || receita.dtEmissao.includes(pesquisar.toLowerCase());
      if (dataInicio?.isValid()) result &&= stringToDate(receita.dtEmissao) >= dataInicio.toDate();
      if (dataFim?.isValid()) result &&= stringToDate(receita.dtEmissao) <= dataFim.toDate();
      return result;
    });
    setFilteredReceitas(filteredReceitas);
  };

  const eraseFilters = () => {
    setDataFim(null);
    setDataInicio(null);
    setPesquisar("");
    handleFilter(receitas, "", null, null);
  };
  return (
    <div className='receita-digital-container'>
      <PageTitle text="Receita digital" marginBottom="15px" />
      {
        !isLoadingData && 
        <>
        { !!receitas.length &&
            <div className="filter-container-receita">
              <div className="filter-input-item-receita">
                <label htmlFor='textField' className='select-label' style={accessibility.textStyleAccessibility} >Pesquisar data ou nome do(a) médico(a) solicitante:</label>
                <TextField
                  id="textField" 
                  variant="outlined"
                  onChange={handlePesquisarChange}
                  value={pesquisar}
                  style={accessibility.textStyleAccessibility}
                  sx={{
                    ...sxTextField,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: accessibility.contrastMode ? 'black' : '',
                      '& fieldset': {
                        borderColor: accessibility.contrastMode ? 'white' : '', 
                      },
                    },
                  }}
                  inputProps={{style: {color: accessibility.contrastMode ? 'white' : ''}}}
                  fullWidth
                  placeholder="Exemplos: 05/04/2024 (data) ou José (nome)"
                  disabled={false}
                />
              </div>
              <div className="filter-date-inicio-receita">
                <label style={accessibility.textStyleAccessibility} className="select-label">Data Início</label>
                <DateInput
                  label='Data início'
                  onChange={setDataInicio}
                  value={dataInicio}
                  maxDate={dayjs(moment().format("YYYY-MM-DD"))}
                  minDate={dayjs("1900-01-01")}
                />
              </div>
              <div className="filter-date-fim-receita">
                <label className="select-label">Data Fim</label>
                <DateInput
                  label='Data fim'
                  onChange={setDataFim}
                  value={dataFim}
                  maxDate={dayjs(moment().format("YYYY-MM-DD"))}
                  minDate={dayjs("1900-01-01")}
                />
              </div>
              <div className="clean-search-receita">
                <div className="search-receita">
                  <Button style={accessibility.buttonStyleAccessibility} variant="contained" size="large" onClick={() => handleFilter(receitas, pesquisar, dataInicio, dataFim)} sx={sxButton} fullWidth>
                    <span style={accessibility.buttonTextStyleAccessibility}>
                      BUSCAR
                    </span>
                  </Button>
                </div>
                <div className="clean-filter-receita">
                  <button className="clean-filter-button-receita" onClick={eraseFilters}>
                    <u style={accessibility.textStyleAccessibility}>Limpar filtros</u>
                  </button>
                </div>
              </div>
            </div>
          }
          {
                   filteredReceitas.length
                   ? <TabelaReceitas data={filteredReceitas} onVisualizarClicked={handleShowPdf}/>
                   : <Typography style={accessibility.textStyleAccessibility} sx={{ marginTop: '20px' }}>
                       {pesquisar.length 
                         ? 'Nenhuma receita encontrada para a pesquisa.' 
                         : 'Nenhuma receita encontrada.'}
                     </Typography>
          }
        </>
      }
      <LoadingApp showLoading={isLoadingReceita || isLoadingData} />
      <ModalFeedBack
        showModal={showErrorModal}
        handleShowModal={() => { setShowErrorModal(false); }}
        icon={'error'}
        textButton={''}
        title={'Atenção'}
        subTitle={errorModalMessage}
      />
    </div>
  )
}

export default ReceitaDigital